/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable @typescript-eslint/no-unsafe-assignment, jsx-a11y/click-events-have-key-events,
 jsx-a11y/no-static-element-interactions */
import React from 'react';
import {
  Select as MuiSelect,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
} from '@mui/material';
import { twMerge } from 'tailwind-merge';
import { CloseIcon, ExpandUpIcon } from 'src/icons';
import { CommonElementProps } from '../types';
import ElementUtils from '../utils';

export type Option<T, V> = {
  label: T;
  value: V;
};

export interface SelectProps<T, V> extends CommonElementProps<V> {
  options: Option<T, V>[];
  value?: V;
  onChange?: (value: V) => void;
  clearable?: boolean;
  classNames?: {
    root?: string,
    label?: string,
  } | undefined,
}

function Select<T extends any, V>(props: SelectProps<T, V>) {
  const {
    options,
    value,
    label,
    onChange,
    error,
    disabled,
    classNames,
    clearable,
    customCssClass,
    placeholder,
  } = props;

  const handleValueChange = (event: SelectChangeEvent<V>) => {
    if (onChange) {
      onChange(event.target.value as V);
    }
  };

  const resetValue = () => {
    if (onChange) {
      onChange(undefined as V);
    }
  };

  return (
    <FormControl
      className={twMerge(
        'flex flex-col justify-start gap-1 min-w-[110px] text-left w-full',
        disabled && 'cursor-not-allowed',
        classNames?.root,
      )}
    >
      {label && (
        <InputLabel
          shrink
          color="secondary"
          error={!!error}
          sx={{
            '&.MuiInputLabel-shrink': {
              transform: 'translate(0, 1.5px) scale(1)', // adjust scale as needed
              fontSize: '1rem', // adjust font size as needed
              lineHeight: '23px', // adjust line height as needed
            },
          }}
          className={twMerge('text-[16px] font-bold static leading-tight tracking-wide text-[#28364C] focus:text-[#28364C]', classNames?.label)}
        >
          {label}
        </InputLabel>
      )}

      <MuiSelect
        placeholder={placeholder || label}
        fullWidth
        value={value}
        onChange={handleValueChange}
        className={twMerge('custom-shadow bg-white border-none h-full', customCssClass)}
        error={!!error}
        disabled={disabled}
        variant="outlined"
        onClick={(event) => {
          event.stopPropagation();
        }}
        sx={{
          '& fieldset': { border: 'none', borderRadius: '8px' },
        }}
        classes={{
          error: 'outline outline-[1px] outline-error',
        }}
        MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
        renderValue={(selectedValue) => {
          const selectedOption = options.find((option) => option.value === selectedValue);
          const labelValue = selectedOption ? selectedOption.label : '';
          if (React.isValidElement(labelValue)) {
            return labelValue;
          }
          return (
            <span className="text-[14px] font-[500]">
              {labelValue as string}
            </span>
          );
        }}
        IconComponent={ExpandUpIcon}
        endAdornment={
          value && clearable ? (
            <div onClick={resetValue} className="bg-white z-10 right-2 top-0 bottom-0 flex justify-center items-center">
              <CloseIcon className="cursor-pointer fill-[#fff] bg-white rounded-full" />
            </div>
          ) : null
        }
      >
        {options.map((option) => (
          <MenuItem value={option.value as any}>
            {option.label as any}
          </MenuItem>
        ))}
      </MuiSelect>
      {error && (
        <div className="text-error text-xs">
          {ElementUtils.renderError(error)}
        </div>
      )}
    </FormControl>
  );
}

Select.defaultProps = {
  value: '',
  onChange: () => { },
  classNames: undefined,
  clearable: false,
};

export default Select;
